import {
  Component,
  OnInit,
  PLATFORM_ID,
  Inject,
  ViewChild,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { Papa } from 'ngx-papaparse';
import {
  DataService,
  PamlabApiServiceService,
  AuthService,
  SnackbarService,
  RouteService,
} from '@/shared/services';
import { Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import {
  APRGraduationSummary,
  FTICCohortStat,
} from '../../../../../server/types';
import { AprTrendGraphComponent } from '../apr-trend-graph/apr-trend-graph.component';
import { EnrollmentAndRetentionByFallsGraphComponent } from '../enrollment-and-retention-by-falls-graph/enrollment-and-retention-by-falls-graph.component';
import { FirstFallGpaGraphComponent } from '../first-fall-gpa-graph/first-fall-gpa-graph.component';
import { StudentsAcademicProgressRateGraphComponent } from '../students-academic-progress-rate-graph/students-academic-progress-rate-graph.component';
import { FourYearGradRateGraphComponent } from '../four-year-grad-rate-graph/four-year-grad-rate-graph.component';

@Component({
  selector: 'apr-graduation-summary-info-content',
  templateUrl: './apr-graduation-summary-info-content.component.html',
  styleUrls: ['./apr-graduation-summary-info-content.component.css'],
  standalone: false,
})
export class AprGraduationSummaryInfoContentComponent implements OnInit {
  @ViewChild('graphsLink') graphsLink!: ElementRef;
  @ViewChild('graphs') graphs!: ElementRef;
  @ViewChild(AprTrendGraphComponent)
  aprTrendGraphComponent!: AprTrendGraphComponent;
  @ViewChild(EnrollmentAndRetentionByFallsGraphComponent)
  enrollmentRetentionChart!: EnrollmentAndRetentionByFallsGraphComponent;
  @ViewChild(FirstFallGpaGraphComponent)
  firstFallGpaChart!: FirstFallGpaGraphComponent;
  @ViewChild(StudentsAcademicProgressRateGraphComponent)
  studentAprChart!: StudentsAcademicProgressRateGraphComponent;
  @ViewChild(FourYearGradRateGraphComponent)
  fourYearGradRateChart!: FourYearGradRateGraphComponent;

  public dataPending!: boolean;
  private subscription: Subscription = new Subscription();

  dataLoaded: boolean = false;
  public data!: APRGraduationSummary;

  public displayedColumns: string[] = [
    'cohort',
    'cohort_size',
    'retention',
    'retention_pct',
    'apr_total',
    'apr_total_pct',
    'apr_stay',
    'apr_stay_pct',
    'apr_migrate',
    'apr_migrate_pct',
    'on_time',
    'on_time_pct',
    'over_time',
    'over_time_pct',
  ];
  public tableData: MatTableDataSource<FTICCohortStat> =
    new MatTableDataSource();

  constructor(
    private dataService: DataService,
    private papa: Papa,
    private apiService: PamlabApiServiceService,
    public authService: AuthService,
    private snackbarService: SnackbarService,
    private renderer: Renderer2,
    private routeService: RouteService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.dataService.getData().subscribe((dataStream) => {
        if (dataStream.data) {
          const data = dataStream.data as APRGraduationSummary;
          this.tableData.data = data.cohort_stats;
          this.data = data;
          this.dataLoaded = true;
          this.renderer.listen(this.graphsLink.nativeElement, 'click', () => {
            this.graphs.nativeElement.scrollIntoView({ behavior: 'smooth' });
          });
        }

        dataStream.error &&
          this.snackbarService.open(dataStream.error.message, 'error');
      }),
    );

    this.subscription.add(
      this.dataService.getPendingData().subscribe((pending) => {
        this.dataPending = pending;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // function to export table data to a csv file
  exportData() {
    if (isPlatformBrowser(this.platformId)) {
      const data: { [key: string]: any }[] = [];
      for (const year of this.tableData.data) {
        data.push({
          cohort: year.cohort,
          cohort_size: year.cohort_size,
          retention: year.retention,
          retention_pct: year.retention_pct,
          apr_total: year.apr_total,
          apr_total_pct: year.apr_total_pct,
          apr_stay: year.apr_stay,
          apr_stay_pct: year.apr_stay_pct,
          apr_migrate: year.apr_migrate,
          apr_migrate_pct: year.apr_migrate_pct,
          on_time: year.on_time,
          on_time_pct: year.on_time_pct,
          over_time: year.over_time,
          over_time_pct: year.over_time_pct,
        });
      }

      const csvData = this.papa.unparse(data);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const pom = document.createElement('a');
      pom.href = url;
      pom.setAttribute('download', 'trend-data.csv');
      pom.click();
    }
  }

  navigateToGlossaryOfTerms(termValue: string) {
    this.routeService.navigateNewTab('/glossary-of-terms', {
      term_value: termValue,
    });
  }
}
